import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/shared/Header";
import { API } from "@/contexts/AuthContext";
import {
  ArrowLeft, CheckCircle2, Loader2, Lock, NotebookPen, Phone, Car, X, Home,
} from "lucide-react";

import QuestionEngine from "@/components/intake/QuestionEngine";
import AdvisorCoach from "@/components/intake/AdvisorCoach";
import InspectionRecommendationCard from "@/components/intake/InspectionRecommendationCard";
import TechPackPreview from "@/components/intake/TechPackPreview";
import ExportActions from "@/components/intake/ExportActions";
import ConversationPhaseStrip from "@/components/intake/ConversationPhaseStrip";
import CallRecapCard from "@/components/intake/CallRecapCard";
import MOTAwarenessPanel from "@/components/intake/MOTAwarenessPanel";
import MOTBookingIntentCard from "@/components/intake/MOTBookingIntentCard";
import MOTSlotSummaryCard from "@/components/intake/MOTSlotSummaryCard";
import CustomerIntakeSummary from "@/components/intake/companion/CustomerIntakeSummary";

/**
 * Yestimate Intake — Live session workspace (V2 — Conversation Assistant).
 *
 * Single-focus progression in the center, calm coaching panels in the right
 * rail. Renders entirely from server-computed signals:
 *   • visible_questions          progressive disclosure
 *   • operational_preparation    live tags + risks
 *   • inspection_recommendation  live downstream workflow preview
 */
export default function IntakeSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [journey, setJourney] = useState(null);
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [trustDismissed, setTrustDismissed] = useState(false);
  // Conversational phase + recap state
  const [phase, setPhase] = useState({ phase: "opening", reason: "", next_phase: null });
  const [recapOpen, setRecapOpen] = useState(false);

  const textTimers = useRef({});

  const loadPhase = async () => {
    try {
      const r = await axios.get(`${API}/intake/sessions/${sessionId}/phase`);
      setPhase({
        phase: r.data?.phase || "opening",
        reason: r.data?.reason || "",
        next_phase: r.data?.next_phase || null,
      });
    } catch {
      /* silent — phase strip just won't update */
    }
  };

  const loadSession = async () => {
    try {
      const sRes = await axios.get(`${API}/intake/sessions/${sessionId}`);
      setSession(sRes.data);
      if (sRes.data?.journey_id) {
        const jRes = await axios.get(`${API}/intake/journeys/${sRes.data.journey_id}`);
        setJourney(jRes.data);
      }
      const pRes = await axios.post(`${API}/intake/sessions/${sessionId}/pack`);
      setPack(pRes.data);
      await loadPhase();
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not load intake session");
      navigate("/app/foh");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadSession();
    // eslint-disable-next-line
  }, [sessionId]);

  const isLocked = session?.status === "complete";
  const visibleQuestions = session?.visible_questions || [];
  const answers = session?.answers || {};
  const opPrep = session?.operational_preparation || pack?.operational_preparation;
  const recommendation = session?.inspection_recommendation || pack?.inspection_recommendation;
  const diagGuidance = session?.diagnostic_guidance || pack?.diagnostic_guidance;

  // Active question — auto-derived from QuestionEngine (first unanswered).
  // We mirror it on the orchestrator so the right rail can render the
  // Conversation Assistant for that question.
  useEffect(() => {
    if (!visibleQuestions.length) return;
    if (activeQuestionId && visibleQuestions.find((q) => q.id === activeQuestionId)) return;
    const firstUnanswered = visibleQuestions.find((q) => isEmpty(answers[q.id]));
    setActiveQuestionId(firstUnanswered ? firstUnanswered.id : visibleQuestions[visibleQuestions.length - 1].id);
  }, [visibleQuestions, answers, activeQuestionId]);

  const activeNode = visibleQuestions.find((q) => q.id === activeQuestionId) || null;

  // ---- Answer handling -----------------------------------------------------

  const submitAnswer = async (qid, value) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/intake/sessions/${sessionId}/answer`, {
        question_id: qid,
        answer: value,
      });
      setSession((prev) => ({
        ...prev,
        answers: res.data.answers,
        visible_questions: res.data.visible_questions,
        operational_preparation: res.data.operational_preparation,
        inspection_recommendation: res.data.inspection_recommendation,
        diagnostic_guidance: res.data.diagnostic_guidance,
      }));
      // Refresh the pack preview (sections + plain_text)
      const pRes = await axios.post(`${API}/intake/sessions/${sessionId}/pack`);
      setPack(pRes.data);
      // Phase may have advanced (e.g. INTELLIGENCE → EXPECT after an answer)
      loadPhase();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const onAnswerChange = (q, value) => {
    if (isLocked) return;
    // Optimistic local update — keeps the UI snappy
    setSession((prev) => ({
      ...prev,
      answers: { ...(prev.answers || {}), [q.id]: value },
    }));
    setActiveQuestionId(q.id);
    if (q.type === "text") {
      if (textTimers.current[q.id]) clearTimeout(textTimers.current[q.id]);
      textTimers.current[q.id] = setTimeout(() => submitAnswer(q.id, value), 500);
    } else {
      submitAnswer(q.id, value);
    }
  };

  const onChangeFohNotes = (txt) => {
    setSession((prev) => ({ ...prev, foh_notes: txt }));
    if (textTimers.current._foh) clearTimeout(textTimers.current._foh);
    textTimers.current._foh = setTimeout(async () => {
      try {
        await axios.patch(`${API}/intake/sessions/${sessionId}`, { foh_notes: txt });
        const pRes = await axios.post(`${API}/intake/sessions/${sessionId}/pack`);
        setPack(pRes.data);
      } catch {
        /* silent */
      }
    }, 600);
  };

  const requiredOutstanding = visibleQuestions.filter(
    (q) => q.required && isEmpty(answers[q.id])
  );

  // Compute progress so we can hide downstream output panels until the
  // FOH has built up enough context. Keeps the early screen calm and focused.
  const requiredQuestions = visibleQuestions.filter((q) => q.required);
  const answeredRequiredCount = requiredQuestions.length - requiredOutstanding.length;
  const requiredProgress = requiredQuestions.length
    ? answeredRequiredCount / requiredQuestions.length
    : 0;
  const showOutputPanels = isLocked || answeredRequiredCount >= 2 || requiredProgress >= 0.5;

  const completeSession = async () => {
    if (requiredOutstanding.length) {
      alert(`Please answer: ${requiredOutstanding.map((q) => q.label).join(", ")}`);
      return;
    }
    setCompleting(true);
    try {
      const res = await axios.post(`${API}/intake/sessions/${sessionId}/complete`);
      setSession(res.data);
      setPack(res.data.pack);
      await loadPhase();
      // The conversation is not over until the recap is delivered to the
      // customer. Open the recap modal so the FOH reads it back before
      // ending the call.
      setRecapOpen(true);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not complete session");
    } finally {
      setCompleting(false);
    }
  };

  const handleRecapDelivered = async () => {
    // Refresh session so `recap_delivered_at` reaches the FE — moves the
    // phase strip from RECAP to closed and hides the lingering recap CTA.
    try {
      const r = await axios.get(`${API}/intake/sessions/${sessionId}`);
      setSession(r.data);
      loadPhase();
    } catch {
      /* silent */
    }
  };

  // ---- Render --------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-6xl mx-auto p-8 text-center text-slate-500">
          <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
          Loading intake session…
        </div>
      </div>
    );
  }
  if (!session || !journey) return null;

  const openingTrust = journey.opening_trust_prompt;
  // Sprint C — journey-level behavioural framing. Surfaces in the Conversation
  // Lead banner as the senior advisor whisper layer. Quietly different per
  // journey: leave-with-us for Service, staged-testing for Diagnostic,
  // safety-first for Breakdown, advisories-aren't-failures for MOT.
  const journeyBehaviour = journey.journey_behaviour || {};
  // Leaf-specific Conversation Lead additions — propagated from
  // triage_config.{leaf}.leaf_core_framing through resolve_triage_path.
  // Surfaces ONLY on the chosen leaf (e.g. mot_only's pairing prompt).
  // Rendered AFTER the journey-level core framing so it lands as the last
  // thing the FOH reads before the live questions begin.
  const leafFraming = Array.isArray(session?.triage_intelligence?.leaf_core_framing)
    ? session.triage_intelligence.leaf_core_framing
    : [];
  const coreFraming = [
    ...(Array.isArray(journeyBehaviour.core_framing) ? journeyBehaviour.core_framing : []),
    ...leafFraming,
  ];
  const pacing = journeyBehaviour.pacing || "steady";
  const retentionSteer = journeyBehaviour.retention_steer || "ok_either_way";

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate("/app/foh")}
            className="text-slate-500 hover:text-slate-800 inline-flex items-center text-[14px]"
            data-testid="intake-back-link"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to FOH Dashboard
          </button>
          <button
            onClick={() => navigate("/app/foh")}
            className="inline-flex items-center text-[13px] font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 px-3 py-1.5 rounded-md shadow-sm transition-colors"
            data-testid="intake-exit-dashboard-btn"
            title="Exit to FOH Dashboard"
          >
            <Home className="w-3.5 h-3.5 mr-1.5" /> FOH Dashboard
          </button>
        </div>

        {/* Conversational phase strip — orientational, not progressive */}
        <ConversationPhaseStrip
          phase={phase.phase}
          reason={phase.reason}
          className="mb-5"
        />

        {/* Header strip — vehicle / customer / status */}
        <div
          className="rounded-xl border border-slate-200 bg-white px-6 py-5 mb-4 flex flex-wrap items-center justify-between gap-4"
          data-testid="intake-header-strip"
        >
          <div className="flex items-center gap-5">
            <RegPlate reg={session.vehicle_reg} />
            <div>
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                {journey.label}
              </p>
              <h1 className="text-[22px] font-bold text-slate-900 leading-tight">
                {session.customer_name}
              </h1>
              {/* Vehicle metadata + contact line */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-[14.5px] text-slate-500">
                {(session.make || session.model || session.year) && (
                  <span className="inline-flex items-center">
                    <Car className="w-3.5 h-3.5 mr-1.5" />
                    {[session.make, session.model, session.year].filter(Boolean).join(" ")}
                  </span>
                )}
                {session.contact_number && (
                  <span className="inline-flex items-center">
                    <Phone className="w-3.5 h-3.5 mr-1.5" /> {session.contact_number}
                  </span>
                )}
                {session.mot_expiry_date && (
                  <MotExpiryPill expiry={session.mot_expiry_date} />
                )}
              </div>
              {/* Triage path breadcrumb badge */}
              {Array.isArray(session.triage_path_labels) && session.triage_path_labels.length > 0 && (
                <div
                  className="mt-2 flex items-center gap-1.5 flex-wrap"
                  data-testid="triage-path-badge"
                >
                  {session.triage_path_labels.map((lbl, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <span className="text-slate-300 text-[12px]">›</span>}
                      <span className="text-[12px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold uppercase tracking-wide">
                        {lbl}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge isLocked={isLocked} saving={saving} />
            {!isLocked && (
              <Button
                onClick={completeSession}
                disabled={completing}
                className="bg-slate-900 hover:bg-slate-800 text-white text-[15px] font-semibold h-11 px-5"
                data-testid="intake-complete-btn"
              >
                {completing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Complete intake
              </Button>
            )}
            {isLocked && session.linked_job_id && (
              <a
                href={`/app/foh/job/${session.linked_job_id}`}
                className="inline-flex items-center gap-2 text-[14px] text-emerald-700 hover:text-emerald-900 font-semibold px-4 py-2 rounded-md bg-emerald-50 border border-emerald-200"
                data-testid="open-linked-job-link"
              >
                <CheckCircle2 className="w-4 h-4" />
                Job card created — open
              </a>
            )}
          </div>
        </div>

        {/* Completion handoff banner — surfaces the workshop handover prominently
            once intake is locked and not yet linked to a job. */}
        {isLocked && !session.linked_job_id && recommendation && (
          <div
            className="rounded-xl border border-teal-200 bg-gradient-to-r from-teal-50 to-white px-6 py-5 mb-4 flex flex-wrap items-center justify-between gap-4"
            data-testid="handover-banner"
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-11 h-11 rounded-lg bg-teal-600 text-white flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[12px] uppercase tracking-[0.18em] text-teal-700 font-semibold">
                  Intake complete — ready for workshop
                </p>
                <p className="text-[17px] text-slate-900 font-bold leading-snug truncate">
                  {recommendation.inspection_type_label}
                </p>
                <p className="text-[14px] text-slate-500 leading-snug truncate">
                  {recommendation.reason}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                // Scroll right rail card into view so the FOH sees the dialog open.
                const el = document.querySelector('[data-testid="open-inspection-preview-btn"]');
                if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.click(); }
              }}
              className="inline-flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white px-5 py-2.5 rounded-md text-[15px] font-semibold shadow-sm"
              data-testid="header-send-to-workshop-btn"
            >
              <ArrowLeft className="w-4 h-4 rotate-180" />
              Send to workshop
            </button>
          </div>
        )}

        {/* Closure recap reminder — when complete but recap not yet delivered.
            Calm, non-blocking. Re-opens the recap modal on click. */}
        {isLocked && !session.recap_delivered_at && (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50/70 px-5 py-3.5 mb-4 flex items-center justify-between gap-4"
            data-testid="recap-reminder-banner"
          >
            <div className="min-w-0">
              <p className="text-[11.5px] uppercase tracking-[0.18em] text-amber-700 font-semibold">
                Closure · Recap pending
              </p>
              <p className="text-[15px] text-amber-900 leading-snug mt-0.5">
                Before ending the call, read the recap back to the customer.
              </p>
            </div>
            <button
              onClick={() => setRecapOpen(true)}
              className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-4 py-2 rounded-md text-[14px] font-semibold shadow-sm flex-shrink-0"
              data-testid="open-recap-btn"
            >
              Read the recap
            </button>
          </div>
        )}

        {/* Conversation Lead — the "senior advisor whisper" layer.
            Stacks: opening trust prompt (calm framing line) → core framing
            sentences (journey-specific behavioural orchestration). Each
            journey carries its own retention steer and pacing implicitly.
            Stress-aware journeys (Breakdown) render in amber, calm steady
            journeys stay teal. */}
        {(openingTrust || coreFraming.length > 0) && !trustDismissed && !isLocked && (
          <div
            className={`rounded-lg border px-5 py-3.5 mb-4 flex items-start gap-4 ${
              pacing === "stress_aware"
                ? "border-amber-200 bg-amber-50/70"
                : "border-teal-100 bg-teal-50/60"
            }`}
            data-testid="opening-trust-prompt"
            data-pacing={pacing}
            data-retention-steer={retentionSteer}
          >
            <span className={`text-[12px] uppercase tracking-[0.18em] font-semibold mt-1 whitespace-nowrap ${
              pacing === "stress_aware" ? "text-amber-700" : "text-teal-700"
            }`}>
              Conversation lead
            </span>
            <div className="flex-1 space-y-2">
              {openingTrust && (
                <p
                  className={`text-[16px] italic leading-relaxed ${
                    pacing === "stress_aware" ? "text-amber-900" : "text-teal-900"
                  }`}
                  data-testid="conversation-lead-opening"
                >
                  "{openingTrust}"
                </p>
              )}
              {coreFraming.map((line, i) => (
                <p
                  key={i}
                  className={`text-[15.5px] leading-relaxed ${
                    pacing === "stress_aware" ? "text-amber-900/90" : "text-teal-900/90"
                  }`}
                  data-testid={`conversation-lead-framing-${i}`}
                >
                  {line}
                </p>
              ))}
            </div>
            <button
              onClick={() => setTrustDismissed(true)}
              className={pacing === "stress_aware" ? "text-amber-700/60 hover:text-amber-900" : "text-teal-700/60 hover:text-teal-900"}
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Persistent SI-Inspection banner removed — Safety guidance now lives
            in the Advisor Coach "Safety" tab so there's a single source of truth. */}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
          {/* Center — Question engine */}
          <main className="space-y-4">
            <QuestionEngine
              visibleQuestions={visibleQuestions}
              answers={answers}
              disabled={isLocked}
              onAnswerChange={onAnswerChange}
            />

            {/* FOH notes — small, last */}
            <section className="rounded-lg border border-slate-200 bg-white">
              <header className="px-5 pt-3.5 pb-2 flex items-center gap-2 border-b border-slate-100">
                <NotebookPen className="w-4 h-4 text-slate-500" />
                <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  Additional FOH notes
                </p>
              </header>
              <div className="px-5 py-3.5">
                <Textarea
                  placeholder="Anything else the technician should know… (urgency, customer mood, recent visits)"
                  value={session.foh_notes || ""}
                  onChange={(e) => onChangeFohNotes(e.target.value)}
                  disabled={isLocked}
                  className="text-[15px] min-h-[88px] resize-none border-slate-200"
                  data-testid="intake-foh-notes"
                />
              </div>
            </section>
          </main>

          {/* Right rail — Advisor Coach (single panel) + downstream output (gated) */}
          <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
            {/* Customer Intake Summary — Phase 2 deterministic recap. Sits
                at the top of the rail so the FOH always has the verbatim
                "say to customer" sentences within glance. */}
            <CustomerIntakeSummary
              sessionId={session.id}
              mode="live"
              testIdPrefix="workspace-recap"
              showWatchOuts
            />
            {/* When intake is locked on an MOT-family journey, the FOH
                no longer needs the live Advisor Coach script or the
                editable booking-intent card — both repeat content from
                the previous step. Show a compact slot summary instead so
                the slot time + anticipated completion are visible without
                scrolling. The Advisor Coach + Booking Intent fall back
                in for the active conversation phase. */}
            {isLocked && isMotJourney(session?.journey_id) ? (
              <MOTSlotSummaryCard sessionId={session.id} />
            ) : (
              <>
                <AdvisorCoach
                  activeNode={activeNode}
                  opPrep={opPrep}
                  guidance={diagGuidance}
                />
                {/* MOT Awareness — surfaces only for MOT-family journeys. Calm
                    continuity layer; never overshadows the live conversation. */}
                {isMotJourney(session?.journey_id) && (
                  <MOTAwarenessPanel
                    sessionId={session.id}
                    mode="foh"
                    data-testid="intake-mot-awareness"
                  />
                )}
                {isMotJourney(session?.journey_id) && (
                  <MOTBookingIntentCard
                    sessionId={session.id}
                    isLocked={isLocked}
                  />
                )}
              </>
            )}
            {showOutputPanels && (
              <>
                <InspectionRecommendationCard
                  session={session}
                  recommendation={recommendation}
                  isLocked={isLocked}
                  onLinked={(jobId) =>
                    setSession((prev) => ({ ...prev, linked_job_id: jobId }))
                  }
                />
                <TechPackPreview pack={pack} />
                <ExportActions sessionId={session.id} vehicleReg={session.vehicle_reg} />
              </>
            )}
          </aside>
        </div>
      </div>

      {/* Call Recap modal — opens on Complete; can be re-opened from the
          handoff banner until the FOH confirms it was delivered */}
      <CallRecapCard
        sessionId={session.id}
        open={recapOpen}
        onClose={() => setRecapOpen(false)}
        onDelivered={handleRecapDelivered}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local atoms
// ---------------------------------------------------------------------------

function RegPlate({ reg }) {
  return (
    <div
      className="inline-flex items-center justify-center bg-yellow-300 border-2 border-slate-900 px-4 py-2 rounded-md font-mono font-bold tracking-wider text-slate-900 text-[20px] shadow-sm"
      data-testid="reg-plate"
    >
      <Car className="w-4 h-4 mr-2 opacity-60" />
      {(reg || "—").toUpperCase()}
    </div>
  );
}

function StatusBadge({ isLocked, saving }) {
  if (isLocked) {
    return (
      <Badge
        variant="outline"
        className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[13px] px-3 py-1"
        data-testid="status-badge-complete"
      >
        <Lock className="w-3.5 h-3.5 mr-1.5" /> Complete
      </Badge>
    );
  }
  if (saving) {
    return (
      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[13px] px-3 py-1">
        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[13px] px-3 py-1">
      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> All saved
    </Badge>
  );
}

/**
 * Live DVLA MOT expiry pill — surfaces on the session header so the FOH
 * never has to ask the customer "when does your MOT run out?". Tone-shifts
 * based on how close expiry is (red ≤14d, amber ≤60d, slate otherwise).
 */
function MotExpiryPill({ expiry }) {
  if (!expiry) return null;
  const d = new Date(expiry);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.round((d.getTime() - Date.now()) / 86400000);
  const formatted = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  let tone = "border-slate-300 bg-slate-50 text-slate-700";
  let label = `MOT expires ${formatted}`;
  if (days < 0) {
    tone = "border-red-300 bg-red-50 text-red-800";
    label = `MOT expired ${formatted}`;
  } else if (days <= 14) {
    tone = "border-red-300 bg-red-50 text-red-800";
    label = `MOT expires in ${days} day${days === 1 ? "" : "s"} (${formatted})`;
  } else if (days <= 60) {
    tone = "border-amber-300 bg-amber-50 text-amber-800";
    label = `MOT due in ${days} days · ${formatted}`;
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[13px] font-semibold ${tone}`}
      data-testid="mot-expiry-pill"
      title="Live from DVLA — no need to ask the customer"
    >
      <Lock className="w-3 h-3 opacity-70" />
      {label}
    </span>
  );
}

function isEmpty(v) {
  return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
}

// MOT-family journeys that should get the FOH Awareness Panel surfaced.
// The panel only renders for these — Service / Diagnostic / Breakdown
// journeys don't get it, keeping the rail focused on what matters per call.
function isMotJourney(journeyId) {
  if (!journeyId) return false;
  return journeyId === "mot" || journeyId === "mot_plus_concern";
}
